import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { addDays } from 'date-fns'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { ApiTokenMutators } from '~/mutators/api-tokens'
import { SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormFieldInput,
  FormFieldSelect,
  toast,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/api-tokens/new')({
  component: RouteComponent,
})

const schema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  description: z.string(),
  expiration: z.enum(['30', '90', '365', 'never']),
})

type FormValues = z.infer<typeof schema>

const expirationOptions = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: 'never', label: 'Never' },
]

function RouteComponent() {
  const navigate = Route.useNavigate()
  const [createdToken, setCreatedToken] = useState<{
    name: string
    token: string
  } | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: 'MCP token',
      description: '',
      expiration: 'never',
    },
  })

  const { create, isPending } = ApiTokenMutators.useCreate({
    onSuccess: (result) => setCreatedToken({ ...result, name: form.getValues('name') }),
  })

  const navigateBack = useCallback(() => {
    navigate({
      to: '/$alias/settings/api-tokens',
    })
  }, [navigate])

  useSettingTitle([
    {
      label: 'API Tokens',
      onClick: navigateBack,
    },
    {
      label: 'New Token',
    },
  ])

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await create({
        name: values.name,
        description: values.description || undefined,
        audience: 'mcp',
        scopes: ['insights:read'],
        expiresAt:
          values.expiration === 'never'
            ? undefined
            : addDays(new Date(), Number(values.expiration)),
      })
    } catch {
      // Mutation errors are presented by ApiTokenMutators.
    }
  })

  const copyCreatedToken = async () => {
    if (!createdToken) return
    await navigator.clipboard.writeText(createdToken.token)
    toast.success('API token copied to clipboard')
  }

  return (
    <SettingsContent spacing="sm">
      <Dialog open={!!createdToken} onOpenChange={(open) => !open && navigateBack()}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>View API Token</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <p className="text-lg">
              View the <strong>{createdToken?.name}</strong> API token below. API tokens
              should be used for accessing Happitu API from external tools.
            </p>
            <p className="text-lg">
              Any action taken by this token will show up as being done by yourself.
            </p>
            <div className="flex items-center gap-3 rounded-md border p-3">
              <code className="min-w-0 flex-1 text-lg break-all">
                {createdToken?.token}
              </code>
              <Button variant="ghost" onClick={copyCreatedToken}>
                Copy
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={navigateBack}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!createdToken && (
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid max-w-200 grid-cols-3 gap-6">
              <div className="col-span-2">
                <FormFieldInput
                  autoFocus
                  control={form.control}
                  name="name"
                  label="Name"
                  required
                />
              </div>
              <FormFieldSelect
                control={form.control}
                name="expiration"
                label="Expiration"
                options={expirationOptions}
              />
            </div>
            <FormFieldInput
              control={form.control}
              name="description"
              label="Description"
              required={false}
            />

            <Button
              type="submit"
              variant="lime"
              loading={isPending}
              loadingLabel="Creating..."
            >
              Create Token
            </Button>
          </form>
        </Form>
      )}
    </SettingsContent>
  )
}
